import "./FlowPage.scss";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as FaIcons from "react-icons/fa";

import flowHeaderFigure from "../img/header-calculator.svg";
import {
  FLOW_COMPONENT_DEFINITIONS,
  FLOW_COMPONENT_SECTIONS,
  getFlowComponentDefinition,
} from "../engineering/flow-components";
import {
  addAppDataToLocalStorage,
  getAppDataFromLocalStorage,
  removeAppDataFromLocalStorage,
  updateAppDataFromLocalStorage,
} from "../functions/localStorageHandlers";
import useOutsideClick from "../functions/useOutsideClick";
import {
  AppData,
  Direction,
  FlowComponentRenderContext,
  FlowComponentType,
  FlowNode,
  FlowWorkspaceData,
} from "../interface/functionalityInterface";
import { HotkeyManager } from "../managers/HotkeyManager";

const CELL_SIZE = 72;
const GRID_LIMIT = 100;
const WORLD_CELLS = GRID_LIMIT * 2 + 1;
const WORLD_SIZE = WORLD_CELLS * CELL_SIZE;
const MIN_ZOOM = 0.55;
const MAX_ZOOM = 2;
const ZOOM_SENSITIVITY = 0.00055;
const DRAG_THRESHOLD = 6;
const FLOW_WORKSPACE_ID = "flow-workspace";

interface PaletteDrag {
  type: FlowComponentType;
  clientX: number;
  clientY: number;
}

interface MoveDrag {
  nodeId: string;
  nodeIds: string[];
  anchorX: number;
  anchorY: number;
  clientX: number;
  clientY: number;
  offsetX: number;
  offsetY: number;
  moved: boolean;
}

interface PanDrag {
  pointerId: number;
  x: number;
  y: number;
}

interface ContextMenuState {
  nodeIds: string[];
  x: number;
  y: number;
}

interface ConnectionMenuState {
  nodeId: string;
  side: Direction;
  x: number;
  y: number;
}

interface CursorState {
  x: number;
  y: number;
  cellX: number;
  cellY: number;
}

interface SelectionBoxState {
  pointerId: number;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  additive: boolean;
}

const DIRECTION_ORDER: Direction[] = ["up", "right", "down", "left"];

const DIRECTION_VECTORS: Record<Direction, { x: number; y: number }> = {
  up: { x: 0, y: -1 },
  right: { x: 1, y: 0 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const isPerpendicular = (first: Direction, second: Direction) => {
  return (
    (first === "up" || first === "down") !==
    (second === "up" || second === "down")
  );
};

const isStraightNode = (type: FlowComponentType) =>
  type === "pipe" ||
  type === "triClamp" ||
  type === "ballValve" ||
  type === "diaphragmValve" ||
  type === "pump";

const getOppositeDirection = (direction: Direction): Direction => {
  switch (direction) {
    case "up":
      return "down";
    case "right":
      return "left";
    case "down":
      return "up";
    case "left":
      return "right";
  }
};

const getPathPoint = (direction: Direction) => {
  switch (direction) {
    case "up":
      return { x: 50, y: 4 };
    case "right":
      return { x: 96, y: 50 };
    case "down":
      return { x: 50, y: 96 };
    case "left":
      return { x: 4, y: 50 };
  }
};

const isHeatExchangerHorizontal = (node: FlowNode) =>
  node.type !== "heatExchanger" ||
  node.flow.from === "left" ||
  node.flow.from === "right";

const getNodeCountValue = (node: FlowNode, key: string, fallback = 0) => {
  const parsed = Number(node.values[key] ?? `${fallback}`);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const getTankSpan = (node: FlowNode) => {
  const topConnections = Math.max(0, Math.min(4, getNodeCountValue(node, "topConnections")));
  const sideConnections = Math.max(0, Math.min(4, getNodeCountValue(node, "sideConnections")));
  const bottomConnections = Math.max(
    0,
    Math.min(4, getNodeCountValue(node, "bottomConnections"))
  );

  return {
    width:
      topConnections > 2 || bottomConnections > 2 || sideConnections > 2 ? 3 : 1,
    height: 1,
  };
};

const getTankPorts = (node: FlowNode): Direction[] => {
  const topConnections = getNodeCountValue(node, "topConnections");
  const sideConnections = getNodeCountValue(node, "sideConnections");
  const bottomConnections = getNodeCountValue(node, "bottomConnections");
  const ports: Direction[] = [];

  if (topConnections > 0) ports.push("up");
  if (sideConnections > 1) ports.push("left");
  if (sideConnections > 0) ports.push("right");
  if (bottomConnections > 0) ports.push("down");

  return ports;
};

const getNodeSpan = (node: FlowNode) => {
  if (node.type === "tank") return getTankSpan(node);

  return { width: 1, height: 1 };
};

const getOccupiedCells = (node: FlowNode) => {
  const span = getNodeSpan(node);
  const occupiedCells = [];

  for (let xIndex = 0; xIndex < span.width; xIndex += 1) {
    for (let yIndex = 0; yIndex < span.height; yIndex += 1) {
      occupiedCells.push({ x: node.x + xIndex, y: node.y + yIndex });
    }
  }

  return occupiedCells;
};

const getPortPosition = (node: FlowNode, side: Direction) => {
  if (node.type === "tank") {
    const span = getTankSpan(node);
    const centerX = node.x + Math.floor((span.width - 1) / 2);
    const centerY = node.y + Math.floor((span.height - 1) / 2);

    switch (side) {
      case "up":
        return { x: centerX, y: node.y - 1 };
      case "down":
        return { x: centerX, y: node.y + span.height };
      case "left":
        return { x: node.x - 1, y: centerY };
      case "right":
        return { x: node.x + span.width, y: centerY };
    }
  }

  if (node.type !== "heatExchanger") {
    const vector = DIRECTION_VECTORS[side];

    return { x: node.x + vector.x, y: node.y + vector.y };
  }

  const vector = DIRECTION_VECTORS[side];
  return { x: node.x + vector.x, y: node.y + vector.y };
};

const createNodeMap = (nodes: FlowNode[]) =>
  new Map(
    nodes.flatMap((node) =>
      getOccupiedCells(node).map((cell) => [`${cell.x},${cell.y}`, node] as const)
    )
  );

const getDirectionFromPoints = (
  fromX: number,
  fromY: number,
  toX: number,
  toY: number
) => {
  if (toX > fromX) return "right";
  if (toX < fromX) return "left";
  if (toY > fromY) return "down";
  return "up";
};

const getGridPosition = (
  clientX: number,
  clientY: number,
  rect: DOMRect,
  pan: { x: number; y: number },
  zoom: number
) => {
  const worldX = (clientX - rect.left - rect.width / 2 - pan.x) / zoom;
  const worldY = (clientY - rect.top - rect.height / 2 - pan.y) / zoom;

  return {
    x: clamp(Math.floor(worldX / CELL_SIZE), -GRID_LIMIT, GRID_LIMIT),
    y: clamp(Math.floor(worldY / CELL_SIZE), -GRID_LIMIT, GRID_LIMIT),
  };
};

const getDefaultValues = (type: FlowComponentType): Record<string, string> => ({
  ...getFlowComponentDefinition(type).defaults,
});

const getFieldsForType = (type: FlowComponentType) =>
  getFlowComponentDefinition(type).fields;

const createPreviewNode = (type: FlowComponentType): FlowNode => {
  const component = getFlowComponentDefinition(type);

  return {
    id: type,
    type,
    x: 0,
    y: 0,
    locked: false,
    startFlow: false,
    label: "",
    color: component.defaultColor,
    flow: component.previewFlow,
    values: getDefaultValues(type),
  };
};

const getNodePorts = (node: FlowNode): Direction[] => {
  if (node.type === "tPipe") {
    return [node.flow.from, ...getOutgoingDirections(node)];
  }
  if (node.type === "heatExchanger") {
    return ["left", "right", "up", "down"];
  }
  if (node.type === "tank") {
    return getTankPorts(node);
  }

  return [node.flow.from, node.flow.to];
};

const getIncomingDirections = (node: FlowNode): Direction[] => {
  if (node.type === "tPipe") return [node.flow.from];
  if (node.type === "heatExchanger") {
    return isHeatExchangerHorizontal(node) ? ["left", "up"] : ["up", "right"];
  }
  if (node.type === "tank") {
    return [node.flow.from];
  }

  return [node.flow.from];
};

const isBallValveClosed = (node: FlowNode) =>
  node.type === "ballValve" && Number(node.values.opening ?? "100") <= 0;

const getOutgoingDirections = (node: FlowNode): Direction[] => {
  if (isBallValveClosed(node)) return [];
  if (node.type === "tPipe") {
    return DIRECTION_ORDER.filter(
      (direction) =>
        direction !== node.flow.from &&
        direction !== getOppositeDirection(node.flow.from)
    );
  }
  if (node.type === "heatExchanger") {
    return isHeatExchangerHorizontal(node) ? ["right", "down"] : ["down", "left"];
  }
  if (node.type === "tank") {
    return [node.flow.to];
  }

  return [node.flow.to];
};

const getConnectionCandidates = (
  x: number,
  y: number,
  nodeMap: Map<string, FlowNode>
) =>
  DIRECTION_ORDER.flatMap((direction) => {
    const vector = DIRECTION_VECTORS[direction];
    const neighbor = nodeMap.get(`${x + vector.x},${y + vector.y}`);

    if (!neighbor) return [];

    const opposite = getOppositeDirection(direction);
    if (!getNodePorts(neighbor).includes(opposite)) return [];

    return [
      {
        side: direction,
        isOutgoing: getOutgoingDirections(neighbor).includes(opposite),
      },
    ];
  });

const chooseStraightFlow = (
  x: number,
  y: number,
  nodeMap: Map<string, FlowNode>
): FlowNode["flow"] => {
  const candidates = getConnectionCandidates(x, y, nodeMap);
  const horizontal = candidates.filter(
    (candidate) => candidate.side === "left" || candidate.side === "right"
  );
  const vertical = candidates.filter(
    (candidate) => candidate.side === "up" || candidate.side === "down"
  );
  const aligned = horizontal.length > 0 ? horizontal : vertical;

  if (aligned.length > 0) {
    const anchor = aligned[0];
    const opposite = getOppositeDirection(anchor.side);

    return anchor.isOutgoing
      ? { from: anchor.side, to: opposite }
      : { from: opposite, to: anchor.side };
  }

  return { from: "left", to: "right" };
};

const chooseBendFlow = (
  x: number,
  y: number,
  nodeMap: Map<string, FlowNode>
): FlowNode["flow"] => {
  const candidates = getConnectionCandidates(x, y, nodeMap);
  const perpendicularConnections = candidates.filter((candidate) =>
    candidates.some(
      (other) =>
        other.side !== candidate.side &&
        isPerpendicular(candidate.side, other.side)
    )
  );

  if (perpendicularConnections.length >= 2) {
    const primary = perpendicularConnections[0];
    const secondary =
      perpendicularConnections.find(
        (candidate) =>
          candidate.side !== primary.side &&
          isPerpendicular(candidate.side, primary.side)
      ) ?? perpendicularConnections[1];

    if (primary.isOutgoing && !secondary.isOutgoing) {
      return { from: primary.side, to: secondary.side };
    }

    if (!primary.isOutgoing && secondary.isOutgoing) {
      return { from: secondary.side, to: primary.side };
    }

    return { from: primary.side, to: secondary.side };
  }

  if (candidates.length === 1) {
    const [candidate] = candidates;
    const turnOptions = DIRECTION_ORDER.filter((direction) =>
      isPerpendicular(direction, candidate.side)
    );

    return candidate.isOutgoing
      ? { from: candidate.side, to: turnOptions[0] }
      : { from: turnOptions[0], to: candidate.side };
  }

  return { from: "left", to: "down" };
};

const createNode = (
  type: FlowComponentType,
  x: number,
  y: number,
  nodeMap: Map<string, FlowNode>,
  connectedSide?: Direction
): FlowNode => ({
  id: `${type}-${Date.now()}-${Math.random().toString(16).slice(2, 7)}`,
  type,
  x,
  y,
  locked: false,
  startFlow: false,
  label: "",
  color: getFlowComponentDefinition(type).defaultColor,
  flow:
    type === "tPipe"
      ? { from: connectedSide ?? "down", to: "right" }
      : type === "heatExchanger"
        ? { from: "left", to: "right" }
      : type === "tank"
        ? { from: connectedSide ?? "up", to: connectedSide === "down" ? "up" : "down" }
      : isStraightNode(type)
        ? chooseStraightFlow(x, y, nodeMap)
        : chooseBendFlow(x, y, nodeMap),
  values: getDefaultValues(type),
});

const getConnectedSides = (node: FlowNode, nodeMap: Map<string, FlowNode>) =>
  getNodePorts(node).filter((side) => {
    const portPosition = getPortPosition(node, side);
    const neighbor = nodeMap.get(`${portPosition.x},${portPosition.y}`);

    if (!neighbor) return false;

    return getNodePorts(neighbor).includes(getOppositeDirection(side));
  });

const getAvailablePorts = (node: FlowNode, nodeMap: Map<string, FlowNode>) =>
  getNodePorts(node).filter(
    (side, index, arr) =>
      arr.indexOf(side) === index &&
      !getConnectedSides(node, nodeMap).includes(side)
  );

const canRotateNode = (node: FlowNode) => !node.locked && node.type !== "tank";

const getInvalidConnectionSides = (node: FlowNode, nodeMap: Map<string, FlowNode>) =>
  getNodePorts(node).filter((side) => {
    const portPosition = getPortPosition(node, side);
    const neighbor = nodeMap.get(`${portPosition.x},${portPosition.y}`);

    if (!neighbor || neighbor.id === node.id) return false;

    return !getNodePorts(neighbor).includes(getOppositeDirection(side));
  });

const getFlowStarts = (nodes: FlowNode[]) => nodes.filter((node) => node.startFlow);

const getNextNodeIds = (node: FlowNode, nodeMap: Map<string, FlowNode>) =>
  getOutgoingDirections(node).flatMap((direction) => {
    const portPosition = getPortPosition(node, direction);
    const neighbor = nodeMap.get(`${portPosition.x},${portPosition.y}`);

    if (!neighbor) return [];

    return getIncomingDirections(neighbor).includes(getOppositeDirection(direction))
      ? [neighbor.id]
      : [];
  });

function FlowNodeGraphic(props: {
  node: FlowNode;
  onRotate?: () => void;
  rotateDisabled?: boolean;
  showRotate?: boolean;
  active?: boolean;
}) {
  const {
    node,
    onRotate,
    rotateDisabled = false,
    showRotate = true,
    active = false,
  } = props;
  const componentColor = node.color || "#9ea4ab";
  const arrowColor = "#5f6966";
  const pulseColor = "#32b889";
  const startPoint = getPathPoint(node.flow.from);
  const endPoint = getPathPoint(node.flow.to);
  const pipeArrowDirection = getDirectionFromPoints(
    startPoint.x,
    startPoint.y,
    endPoint.x,
    endPoint.y
  );
  const isHorizontal =
    (node.flow.from === "left" || node.flow.from === "right") &&
    (node.flow.to === "left" || node.flow.to === "right");
  const gradientId = `flow-gradient-${node.id}`;
  const maskId = `flow-mask-${node.id}`;
  const isHeatExchangerVertical =
    node.type === "heatExchanger" && !isHeatExchangerHorizontal(node);
  const componentDefinition = getFlowComponentDefinition(node.type);
  const renderContext: FlowComponentRenderContext = {
    node,
    componentColor,
    arrowColor,
    pulseColor,
    startPoint,
    endPoint,
    pipeArrowDirection,
    isHorizontal,
    isHeatExchangerVertical,
    isBallValveClosed: isBallValveClosed(node),
  };

  return (
    <div className={`flow-node flow-node--${node.type}`}>
      <svg viewBox="0 0 100 100" aria-hidden="true">
        {active ? (
          <defs>
            <linearGradient id={gradientId}>
              <stop offset="0" stopColor="#ffffff" stopOpacity="0" />
              <stop offset="0.6" stopColor="#ffffff" stopOpacity="1" />
              <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
            </linearGradient>
            <mask id={maskId}>
              <rect x="-120" y="0" width="140" height="100" fill={`url(#${gradientId})`}>
                <animate
                  attributeName="x"
                  from="-120"
                  to="120"
                  dur="1.8s"
                  repeatCount="indefinite"
                />
              </rect>
            </mask>
          </defs>
        ) : null}
        {componentDefinition.renderGraphic(renderContext)}
        {active && componentDefinition.renderAnimatedFlow ? (
          <g mask={`url(#${maskId})`}>
            {componentDefinition.renderAnimatedFlow(renderContext)}
          </g>
        ) : null}
      </svg>

      {showRotate ? (
        <button
          className="flow-node__rotate rectangular-button smallest inverse-color"
          type="button"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            onRotate?.();
          }}
          disabled={rotateDisabled}
          title={rotateDisabled ? "Rotation unavailable" : "Rotate component"}
        >
          <FaIcons.FaSyncAlt />
        </button>
      ) : null}
    </div>
  );
}

export default function FlowPage() {
  const boundaryRef = useRef<HTMLDivElement>(null);
  const floatingPanelRef = useRef<HTMLDivElement>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const connectionMenuRef = useRef<HTMLDivElement>(null);
  const [nodes, setNodes] = useState<FlowNode[]>([]);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [paletteDrag, setPaletteDrag] = useState<PaletteDrag | null>(null);
  const [moveDrag, setMoveDrag] = useState<MoveDrag | null>(null);
  const [selectionBox, setSelectionBox] = useState<SelectionBoxState | null>(null);
  const [dropCell, setDropCell] = useState<{ x: number; y: number } | null>(null);
  const [panDrag, setPanDrag] = useState<PanDrag | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [connectionMenu, setConnectionMenu] = useState<ConnectionMenuState | null>(
    null
  );
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  const [componentSearch, setComponentSearch] = useState("");
  const [cursor, setCursor] = useState<CursorState | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackIndex, setPlaybackIndex] = useState(0);
  const historyRef = useRef<FlowNode[][]>([]);
  const redoRef = useRef<FlowNode[][]>([]);
  const restoringHistoryRef = useRef(false);

  const nodeMap = useMemo(() => createNodeMap(nodes), [nodes]);
  const selectedNodes = useMemo(
    () => nodes.filter((node) => selectedNodeIds.includes(node.id)),
    [nodes, selectedNodeIds]
  );
  const selectedNode =
    selectedNodes.length === 1 ? selectedNodes[0] : null;
  const selectedNodeType =
    selectedNodes.length > 0 &&
    selectedNodes.every((node) => node.type === selectedNodes[0].type)
      ? selectedNodes[0].type
      : null;
  const panelFields = useMemo(() => {
    const sharedFields = [
      { key: "label", label: "Label", inputType: "text" as const },
      { key: "color", label: "Color", inputType: "color" as const },
    ];

    if (!selectedNodeType) return sharedFields;

    return [
      { key: "label", label: "Label" },
      ...getFieldsForType(selectedNodeType),
    ];
  }, [selectedNodeType]);
  const filteredSidebarSections = useMemo(() => {
    const normalizedSearch = componentSearch.trim().toLowerCase();

    return FLOW_COMPONENT_SECTIONS.map((section) => ({
      ...section,
      components: section.components.filter(
        (component) =>
          normalizedSearch.length === 0 ||
          component.title.toLowerCase().includes(normalizedSearch)
      ),
    })).filter((section) => section.components.length > 0);
  }, [componentSearch]);

  const playbackSteps = useMemo(() => {
    if (nodes.length === 0) return [] as string[][];

    const starts = getFlowStarts(nodes).sort((first, second) =>
      first.y === second.y ? first.x - second.x : first.y - second.y
    );
    const visited = new Set<string>();
    const steps: string[][] = [];
    let frontier = starts.map((node) => node.id);

    while (frontier.length > 0) {
      const uniqueFrontier = frontier.filter((id, index, arr) => arr.indexOf(id) === index);
      const currentStep = uniqueFrontier.filter((id) => !visited.has(id));

      if (currentStep.length === 0) break;

      currentStep.forEach((id) => visited.add(id));
      steps.push(currentStep);

      frontier = currentStep.flatMap((id) => {
        const current = nodes.find((node) => node.id === id);
        return current ? getNextNodeIds(current, nodeMap) : [];
      });
    }

    return steps;
  }, [nodeMap, nodes]);

  useEffect(() => {
    const existingWorkspace = (
      getAppDataFromLocalStorage([AppData.FLOW]) as FlowWorkspaceData[] | undefined
    )?.find((data) => data.id === FLOW_WORKSPACE_ID);

    if (existingWorkspace) {
      setNodes(existingWorkspace.nodes as FlowNode[]);
      setPan(existingWorkspace.pan);
      setZoom(existingWorkspace.zoom);
    }

    setIsLoaded(true);
  }, []);

  useEffect(() => {
    if (!isLoaded) return;

    const nextWorkspace: FlowWorkspaceData = {
      id: FLOW_WORKSPACE_ID,
      type: AppData.FLOW,
      nodes,
      pan,
      zoom,
    };
    const existingWorkspace = (
      getAppDataFromLocalStorage([AppData.FLOW]) as FlowWorkspaceData[] | undefined
    )?.find((data) => data.id === FLOW_WORKSPACE_ID);

    if (existingWorkspace) {
      updateAppDataFromLocalStorage(FLOW_WORKSPACE_ID, nextWorkspace);
    } else {
      addAppDataToLocalStorage(nextWorkspace);
    }
  }, [isLoaded, nodes, pan, zoom]);

  useOutsideClick(floatingPanelRef, () => {
    setSelectedNodeIds([]);
  });

  useOutsideClick(contextMenuRef, () => {
    setContextMenu(null);
  });

  useOutsideClick(connectionMenuRef, () => {
    setConnectionMenu(null);
  });

  useEffect(() => {
    if (!isLoaded) return;
    if (restoringHistoryRef.current) {
      restoringHistoryRef.current = false;
      return;
    }

    historyRef.current.push(JSON.parse(JSON.stringify(nodes)) as FlowNode[]);
    if (historyRef.current.length > 80) {
      historyRef.current.shift();
    }
    redoRef.current = [];
  }, [isLoaded, nodes]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isUndo = HotkeyManager.matches(event, { key: "z", metaKey: true }) ||
        HotkeyManager.matches(event, { key: "z", ctrlKey: true });
      const isRedo =
        HotkeyManager.matches(event, { key: "z", metaKey: true, shiftKey: true }) ||
        HotkeyManager.matches(event, { key: "z", ctrlKey: true, shiftKey: true }) ||
        HotkeyManager.matches(event, { key: "y", ctrlKey: true });
      const isDeleteSelection =
        HotkeyManager.matches(event, { key: "backspace", metaKey: true }) ||
        HotkeyManager.matches(event, { key: "delete" });

      if (isUndo) {
        event.preventDefault();
        if (historyRef.current.length <= 1) return;
        const current = historyRef.current.pop();
        const previous = historyRef.current[historyRef.current.length - 1];
        if (!current || !previous) return;
        redoRef.current.push(current);
        restoringHistoryRef.current = true;
        setNodes(JSON.parse(JSON.stringify(previous)) as FlowNode[]);
      }

      if (isRedo) {
        event.preventDefault();
        const next = redoRef.current.pop();
        if (!next) return;
        restoringHistoryRef.current = true;
        setNodes(JSON.parse(JSON.stringify(next)) as FlowNode[]);
        historyRef.current.push(JSON.parse(JSON.stringify(next)) as FlowNode[]);
      }

      if (isDeleteSelection && selectedNodeIds.length > 0) {
        event.preventDefault();
        setNodes((currentNodes) =>
          currentNodes.filter((node) => !selectedNodeIds.includes(node.id))
        );
        setSelectedNodeIds([]);
        setContextMenu(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedNodeIds]);

  useEffect(() => {
    if (!boundaryRef.current) return;

    const boundary = boundaryRef.current;
    const stopGesture = (event: Event) => event.preventDefault();

    boundary.addEventListener("gesturestart", stopGesture);
    boundary.addEventListener("gesturechange", stopGesture);
    boundary.addEventListener("gestureend", stopGesture);

    return () => {
      boundary.removeEventListener("gesturestart", stopGesture);
      boundary.removeEventListener("gesturechange", stopGesture);
      boundary.removeEventListener("gestureend", stopGesture);
    };
  }, []);

  useEffect(() => {
    if (!isPlaying || playbackSteps.length === 0) return;

    const interval = window.setInterval(() => {
      setPlaybackIndex((current) =>
        current >= playbackSteps.length - 1 ? playbackSteps.length - 1 : current + 1
      );
    }, 650);

    return () => window.clearInterval(interval);
  }, [isPlaying, playbackSteps.length]);

  useEffect(() => {
    if (playbackIndex >= playbackSteps.length - 1 && playbackSteps.length > 0) {
      setIsPlaying(false);
    }
  }, [playbackIndex, playbackSteps.length]);

  useEffect(() => {
    if (playbackSteps.length === 0) {
      setPlaybackIndex(0);
      setIsPlaying(false);
      return;
    }

    if (playbackIndex > playbackSteps.length - 1) {
      setPlaybackIndex(Math.max(playbackSteps.length - 1, 0));
    }
  }, [playbackIndex, playbackSteps.length]);

  const updateDropCell = useCallback(
    (clientX: number, clientY: number) => {
      if (!boundaryRef.current) {
        setDropCell(null);
        return null;
      }

      const rect = boundaryRef.current.getBoundingClientRect();
      const isInside =
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom;

      if (!isInside) {
        setDropCell(null);
        return null;
      }

      const nextCell = getGridPosition(clientX, clientY, rect, pan, zoom);
      setDropCell(nextCell);
      return nextCell;
    },
    [pan, zoom]
  );

  const updateCursor = useCallback(
    (clientX: number, clientY: number) => {
      if (!boundaryRef.current) {
        setCursor(null);
        return;
      }

      const rect = boundaryRef.current.getBoundingClientRect();
      const isInside =
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom;

      if (!isInside) {
        setCursor(null);
        return;
      }

      const nextCell = getGridPosition(clientX, clientY, rect, pan, zoom);

      setCursor({
        x: clientX - rect.left,
        y: clientY - rect.top,
        cellX: nextCell.x,
        cellY: nextCell.y,
      });
    },
    [pan, zoom]
  );

  const getWorldPoint = useCallback(
    (clientX: number, clientY: number, rect: DOMRect) => ({
      x: (clientX - rect.left - rect.width / 2 - pan.x) / zoom,
      y: (clientY - rect.top - rect.height / 2 - pan.y) / zoom,
    }),
    [pan, zoom]
  );

  const placeNode = useCallback(
    (type: FlowComponentType, nextCell: { x: number; y: number } | null) => {
      if (!nextCell) return;
      const nextNode = createNode(type, nextCell.x, nextCell.y, nodeMap);
      const overlaps = getOccupiedCells(nextNode).some((cell) =>
        nodeMap.has(`${cell.x},${cell.y}`)
      );
      if (overlaps) return;

      setNodes((currentNodes) => [...currentNodes, nextNode]);
    },
    [nodeMap]
  );

  useEffect(() => {
    if (!paletteDrag) return;

    const handlePointerMove = (event: PointerEvent) => {
      setPaletteDrag((currentDrag) =>
        currentDrag
          ? { ...currentDrag, clientX: event.clientX, clientY: event.clientY }
          : null
      );
      updateDropCell(event.clientX, event.clientY);
      updateCursor(event.clientX, event.clientY);
    };

    const handlePointerUp = (event: PointerEvent) => {
      placeNode(paletteDrag.type, updateDropCell(event.clientX, event.clientY));
      setPaletteDrag(null);
      setDropCell(null);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp, { once: true });

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [paletteDrag, placeNode, updateCursor, updateDropCell]);

  useEffect(() => {
    if (!moveDrag) return;

    const handlePointerMove = (event: PointerEvent) => {
      const movedEnough =
        Math.abs(event.clientX - moveDrag.clientX) > DRAG_THRESHOLD ||
        Math.abs(event.clientY - moveDrag.clientY) > DRAG_THRESHOLD;

      setMoveDrag((currentDrag) =>
        currentDrag
          ? {
              ...currentDrag,
              clientX: event.clientX,
              clientY: event.clientY,
              moved: currentDrag.moved || movedEnough,
            }
          : null
      );
      updateDropCell(event.clientX, event.clientY);
      updateCursor(event.clientX, event.clientY);
    };

    const handlePointerUp = (event: PointerEvent) => {
      const nextCell = updateDropCell(event.clientX, event.clientY);

      if (moveDrag.moved && nextCell) {
        setNodes((currentNodes) => {
          const deltaX = nextCell.x - moveDrag.anchorX;
          const deltaY = nextCell.y - moveDrag.anchorY;
          const movingIds = new Set(moveDrag.nodeIds);
          const movingNodes = currentNodes.filter((node) => movingIds.has(node.id));

          const blocked = movingNodes.some((node) => {
            if (node.locked) return true;
            const movedNode = { ...node, x: node.x + deltaX, y: node.y + deltaY };

            return getOccupiedCells(movedNode).some((cell) =>
              currentNodes.some(
                (otherNode) =>
                  !movingIds.has(otherNode.id) &&
                  getOccupiedCells(otherNode).some(
                    (otherCell) => otherCell.x === cell.x && otherCell.y === cell.y
                  )
              )
            );
          });

          if (blocked) return currentNodes;

          return currentNodes.map((node) =>
            movingIds.has(node.id)
              ? { ...node, x: node.x + deltaX, y: node.y + deltaY }
              : node
          );
        });
      } else {
        setSelectedNodeIds(moveDrag.nodeIds);
      }

      setMoveDrag(null);
      setDropCell(null);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp, { once: true });

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [moveDrag, updateCursor, updateDropCell]);

  useEffect(() => {
    if (!selectionBox || !boundaryRef.current) return;

    const rect = boundaryRef.current.getBoundingClientRect();

    const handlePointerMove = (event: PointerEvent) => {
      setSelectionBox((currentBox) =>
        currentBox
          ? { ...currentBox, currentX: event.clientX - rect.left, currentY: event.clientY - rect.top }
          : null
      );
      updateCursor(event.clientX, event.clientY);
    };

    const handlePointerUp = (event: PointerEvent) => {
      const endX = event.clientX - rect.left;
      const endY = event.clientY - rect.top;
      const startWorld = getWorldPoint(
        rect.left + selectionBox.startX,
        rect.top + selectionBox.startY,
        rect
      );
      const endWorld = getWorldPoint(event.clientX, event.clientY, rect);
      const minX = Math.min(startWorld.x, endWorld.x);
      const maxX = Math.max(startWorld.x, endWorld.x);
      const minY = Math.min(startWorld.y, endWorld.y);
      const maxY = Math.max(startWorld.y, endWorld.y);

      const hits = nodes
        .filter((node) => {
          const span = getNodeSpan(node);
          const nodeLeft = node.x * CELL_SIZE;
          const nodeTop = node.y * CELL_SIZE;
          const nodeRight = nodeLeft + CELL_SIZE * span.width;
          const nodeBottom = nodeTop + CELL_SIZE * span.height;

          return (
            nodeRight >= minX &&
            nodeLeft <= maxX &&
            nodeBottom >= minY &&
            nodeTop <= maxY
          );
        })
        .map((node) => node.id);

      if (Math.abs(endX - selectionBox.startX) <= DRAG_THRESHOLD &&
          Math.abs(endY - selectionBox.startY) <= DRAG_THRESHOLD) {
        if (!selectionBox.additive) setSelectedNodeIds([]);
      } else if (selectionBox.additive) {
        setSelectedNodeIds((current) =>
          Array.from(new Set([...current, ...hits]))
        );
      } else {
        setSelectedNodeIds(hits);
      }

      setSelectionBox(null);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp, { once: true });

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [getWorldPoint, nodes, selectionBox, updateCursor]);

  const handleBoundaryWheel = useCallback(
    (event: WheelEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (!boundaryRef.current) return;

      const rect = boundaryRef.current.getBoundingClientRect();
      const cursorX = event.clientX - rect.left;
      const cursorY = event.clientY - rect.top;
      const shouldZoom = event.shiftKey;

      if (shouldZoom) {
        const nextZoom = clamp(
          zoom * Math.exp(-event.deltaY * ZOOM_SENSITIVITY),
          MIN_ZOOM,
          MAX_ZOOM
        );

        if (nextZoom === zoom) return;

        const worldX = (cursorX - rect.width / 2 - pan.x) / zoom;
        const worldY = (cursorY - rect.height / 2 - pan.y) / zoom;

        setZoom(nextZoom);
        setPan({
          x: cursorX - rect.width / 2 - worldX * nextZoom,
          y: cursorY - rect.height / 2 - worldY * nextZoom,
        });
        return;
      }

      setPan((currentPan) => ({
        x: currentPan.x - event.deltaX,
        y: currentPan.y - event.deltaY,
      }));
    },
    [pan, zoom]
  );

  useEffect(() => {
    if (!boundaryRef.current) return;

    const boundary = boundaryRef.current;
    boundary.addEventListener("wheel", handleBoundaryWheel, { passive: false });

    return () => {
      boundary.removeEventListener("wheel", handleBoundaryWheel as EventListener);
    };
  }, [handleBoundaryWheel]);

  const handleBoundaryPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (
      target.closest(".flow-page__node") ||
      target.closest(".flow-page__boundary-toolbar") ||
      target.closest(".flow-page__floating-panel") ||
      target.closest(".flow-page__connection-menu") ||
      target.closest(".flow-page__context-menu")
    ) {
      return;
    }

    if (event.button !== 0) return;

    if (event.altKey) {
      event.preventDefault();
      setPanDrag({
        pointerId: event.pointerId,
        x: event.clientX,
        y: event.clientY,
      });
      event.currentTarget.setPointerCapture(event.pointerId);
      return;
    }

    event.preventDefault();
    setContextMenu(null);
    setConnectionMenu(null);
    setSelectionBox({
      pointerId: event.pointerId,
      startX: event.clientX - event.currentTarget.getBoundingClientRect().left,
      startY: event.clientY - event.currentTarget.getBoundingClientRect().top,
      currentX: event.clientX - event.currentTarget.getBoundingClientRect().left,
      currentY: event.clientY - event.currentTarget.getBoundingClientRect().top,
      additive: event.shiftKey,
    });
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleBoundaryPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    updateCursor(event.clientX, event.clientY);

    if (selectionBox && selectionBox.pointerId === event.pointerId) return;
    if (!panDrag || panDrag.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - panDrag.x;
    const deltaY = event.clientY - panDrag.y;

    setPan((currentPan) => ({
      x: currentPan.x + deltaX,
      y: currentPan.y + deltaY,
    }));
    setPanDrag({
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
    });
  };

  const handleBoundaryPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (panDrag && panDrag.pointerId === event.pointerId) {
      event.currentTarget.releasePointerCapture(event.pointerId);
      setPanDrag(null);
    }

    if (selectionBox && selectionBox.pointerId === event.pointerId) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleStartPaletteDrag = (
    event: React.PointerEvent<HTMLButtonElement>,
    type: FlowComponentType
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedNodeIds([]);
    setContextMenu(null);
    setConnectionMenu(null);
    setPaletteDrag({ type, clientX: event.clientX, clientY: event.clientY });
    updateDropCell(event.clientX, event.clientY);
  };

  const handleRotateNode = (nodeId: string) => {
    setNodes((currentNodes) => {
      const currentMap = createNodeMap(currentNodes);

      return currentNodes.map((node) => {
        if (node.id !== nodeId) return node;
        if (node.locked) return node;

        if (isStraightNode(node.type)) {
          const isHorizontal =
            (node.flow.from === "left" || node.flow.from === "right") &&
            (node.flow.to === "left" || node.flow.to === "right");

          return {
            ...node,
            flow: isHorizontal ? { from: "up", to: "down" } : { from: "left", to: "right" },
          };
        }

        if (node.type === "tPipe") {
          const teeRotationOrder: Direction[] = ["down", "left", "up", "right"];
          const nextIndex =
            (teeRotationOrder.indexOf(node.flow.from) + 1) % teeRotationOrder.length;

          return {
            ...node,
            flow: { from: teeRotationOrder[nextIndex], to: "right" },
          };
        }

        if (node.type === "heatExchanger") {
          const isHorizontalExchanger = isHeatExchangerHorizontal(node);

          return {
            ...node,
            flow: isHorizontalExchanger
              ? { from: "up", to: "down" }
              : { from: "left", to: "right" },
          };
        }

        const connectedSides = getConnectedSides(node, currentMap);
        const anchorSide = connectedSides[0] ?? node.flow.from;
        const otherSide =
          node.flow.from === anchorSide ? node.flow.to : node.flow.from;
        const rotateOptions = DIRECTION_ORDER.filter((direction) =>
          isPerpendicular(direction, anchorSide)
        );
        const currentIndex = rotateOptions.indexOf(otherSide);
        const nextOtherSide =
          rotateOptions[(currentIndex + 1 + rotateOptions.length) % rotateOptions.length];

        return node.flow.from === anchorSide
          ? { ...node, flow: { from: anchorSide, to: nextOtherSide } }
          : { ...node, flow: { from: nextOtherSide, to: anchorSide } };
      });
    });
  };

  const handleNodePointerDown = (
    event: React.PointerEvent<HTMLDivElement>,
    node: FlowNode
  ) => {
    if (event.button !== 0 || event.ctrlKey) return;

    const target = event.target as HTMLElement;
    if (target.closest(".flow-node__rotate") || target.closest(".flow-page__port-hint")) {
      return;
    }

    if (event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      setSelectedNodeIds((current) =>
        current.includes(node.id)
          ? current.filter((id) => id !== node.id)
          : [...current, node.id]
      );
      return;
    }

    if (node.locked) {
      setSelectedNodeIds([node.id]);
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    const nodeRect = event.currentTarget.getBoundingClientRect();
    const nodeIds = selectedNodeIds.includes(node.id) ? selectedNodeIds : [node.id];
    if (!selectedNodeIds.includes(node.id)) {
      setSelectedNodeIds([node.id]);
    }
    setMoveDrag({
      nodeId: node.id,
      nodeIds,
      anchorX: node.x,
      anchorY: node.y,
      clientX: event.clientX,
      clientY: event.clientY,
      offsetX: event.clientX - nodeRect.left,
      offsetY: event.clientY - nodeRect.top,
      moved: false,
    });
  };

  const handleNodeContextMenu = (
    event: React.MouseEvent<HTMLDivElement>,
    node: FlowNode
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const nextSelection =
      selectedNodeIds.includes(node.id) && selectedNodeIds.length > 0
        ? selectedNodeIds
        : [node.id];
    setSelectedNodeIds(nextSelection);
    setConnectionMenu(null);
    setContextMenu({
      nodeIds: nextSelection,
      x: event.clientX,
      y: event.clientY,
    });
  };

  const handleNodeValueChange = (field: string, value: string) => {
    setNodes((currentNodes) =>
      currentNodes.map((node) =>
        selectedNodeIds.includes(node.id)
          ? {
              ...node,
              label: field === "label" ? value : node.label,
              color: field === "color" ? value : node.color,
              values: { ...node.values, [field]: value },
            }
          : node
      )
    );
  };

  const handleToggleBallValve = (nodeId: string) => {
    setNodes((currentNodes) =>
      currentNodes.map((node) => {
        if (node.id !== nodeId || node.type !== "ballValve") return node;

        const currentOpening = Number(node.values.opening ?? "100");
        const nextOpening = currentOpening <= 0 ? "100" : "0";

        return {
          ...node,
          values: { ...node.values, opening: nextOpening },
        };
      })
    );
    setPlaybackIndex(0);
    setIsPlaying(false);
  };

  const handleRemoveNode = (nodeIds: string[]) => {
    setNodes((currentNodes) =>
      currentNodes.filter((node) => !nodeIds.includes(node.id))
    );
    if (nodeIds.some((nodeId) => selectedNodeIds.includes(nodeId))) setSelectedNodeIds([]);
    setContextMenu(null);
  };

  const handleToggleLockNode = (nodeIds: string[]) => {
    setNodes((currentNodes) =>
      currentNodes.map((node) =>
        nodeIds.includes(node.id) ? { ...node, locked: !node.locked } : node
      )
    );
    setContextMenu(null);
  };

  const handleToggleStartFlow = (nodeIds: string[]) => {
    setNodes((currentNodes) =>
      currentNodes.map((node) =>
        nodeIds.includes(node.id) ? { ...node, startFlow: !node.startFlow } : node
      )
    );
    setPlaybackIndex(0);
    setIsPlaying(false);
    setContextMenu(null);
  };

  const handleOpenConnectionMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    nodeId: string,
    side: Direction
  ) => {
    event.stopPropagation();
    setSelectedNodeIds([nodeId]);
    setContextMenu(null);
    setConnectionMenu({
      nodeId,
      side,
      x: event.clientX,
      y: event.clientY,
    });
  };

  const handleAddConnectedNode = (type: FlowComponentType) => {
    if (!connectionMenu) return;

    const owner = nodes.find((node) => node.id === connectionMenu.nodeId);
    if (!owner) return;

    const vector = DIRECTION_VECTORS[connectionMenu.side];
    const nextCell = {
      x: owner.x + vector.x,
      y: owner.y + vector.y,
    };

    if (nodeMap.has(`${nextCell.x},${nextCell.y}`)) return;

    const nextNode = createNode(
      type,
      nextCell.x,
      nextCell.y,
      nodeMap,
      getOppositeDirection(connectionMenu.side)
    );
    const overlaps = getOccupiedCells(nextNode).some((cell) =>
      nodeMap.has(`${cell.x},${cell.y}`)
    );
    if (overlaps) return;
    setNodes((currentNodes) => [...currentNodes, nextNode]);
    setConnectionMenu(null);
  };

  const handleResetPan = () => setPan({ x: 0, y: 0 });
  const handleResetZoom = () => setZoom(1);

  const activeNodeIds = useMemo(
    () =>
      new Set(
        playbackSteps.slice(0, playbackIndex + 1).flatMap((step) => step)
      ),
    [playbackIndex, playbackSteps]
  );

  const selectedNodeScreenPosition = useMemo(() => {
    if (selectedNodes.length === 0 || !boundaryRef.current) return null;

    const anchorNode = selectedNodes[0];
    const span = getNodeSpan(anchorNode);

    const rect = boundaryRef.current.getBoundingClientRect();

    return {
      left:
        rect.width / 2 +
        pan.x +
        anchorNode.x * CELL_SIZE * zoom +
        CELL_SIZE * span.width * zoom +
        18,
      top: rect.height / 2 + pan.y + anchorNode.y * CELL_SIZE * zoom + 18,
    };
  }, [pan, selectedNodes, zoom]);

  return (
    <div className="flow-page">
      <header>
        <figure className="flow-page__logo">
          <img src={flowHeaderFigure} alt="flow" />
        </figure>
        <h1>flow</h1>
        <p>
          Build piping step by step inside a centered coordinate boundary with
          draggable components and storage-backed state.
        </p>
      </header>

      <section className="flow-page__workspace-section">
        <div className="flow-page__editor">
          <div
            className={`flow-page__boundary ${
              panDrag ? "flow-page__boundary--panning" : ""
            } ${paletteDrag || moveDrag ? "flow-page__boundary--dragging" : ""}`}
            ref={boundaryRef}
            onPointerDown={handleBoundaryPointerDown}
            onPointerMove={handleBoundaryPointerMove}
            onPointerUp={handleBoundaryPointerUp}
            onPointerLeave={() => setCursor(null)}
          >
            <div className="flow-page__boundary-toolbar">
              <button
                type="button"
                className="flow-page__toggle-chip"
                onClick={handleResetPan}
              >
                <span>Pan drag</span>
                <span
                  className="flow-page__toggle-chip__reset"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleResetPan();
                  }}
                >
                  reset
                </span>
              </button>

              <button
                type="button"
                className="flow-page__toggle-chip"
                onClick={handleResetZoom}
              >
                <span>Shift zoom</span>
                <span
                  className="flow-page__toggle-chip__reset"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleResetZoom();
                  }}
                >
                  reset
                </span>
              </button>

              <div className="flow-page__coords-chip">
                X: {cursor?.cellX ?? 0} Y: {cursor?.cellY ?? 0}
              </div>
            </div>

            {cursor ? (
              <div className="flow-page__crosshair" aria-hidden="true">
                <span
                  className="flow-page__crosshair-line flow-page__crosshair-line--vertical"
                  style={{ left: cursor.x }}
                ></span>
                <span
                  className="flow-page__crosshair-line flow-page__crosshair-line--horizontal"
                  style={{ top: cursor.y }}
                ></span>
              </div>
            ) : null}

            {selectionBox ? (
              <div
                className="flow-page__selection-box"
                style={{
                  left: Math.min(selectionBox.startX, selectionBox.currentX),
                  top: Math.min(selectionBox.startY, selectionBox.currentY),
                  width: Math.abs(selectionBox.currentX - selectionBox.startX),
                  height: Math.abs(selectionBox.currentY - selectionBox.startY),
                }}
              ></div>
            ) : null}

            <div
              className="flow-page__world"
              style={{
                width: `${WORLD_SIZE}px`,
                height: `${WORLD_SIZE}px`,
                transform: `translate(calc(-50% + ${pan.x}px), calc(-50% + ${pan.y}px)) scale(${zoom})`,
              }}
            >
              <div className="flow-page__grid"></div>

              {dropCell ? (
                <div
                  className={`flow-page__drop-preview ${
                    nodeMap.has(`${dropCell.x},${dropCell.y}`)
                      ? "flow-page__drop-preview--blocked"
                      : ""
                  }`}
                  style={{
                    left: `calc(50% + ${dropCell.x * CELL_SIZE}px)`,
                    top: `calc(50% + ${dropCell.y * CELL_SIZE}px)`,
                  }}
                >
                  <span>{paletteDrag ? paletteDrag.type : "move"}</span>
                </div>
              ) : null}

              {nodes.map((node) => {
                const showNode =
                  !(moveDrag && moveDrag.nodeId === node.id && moveDrag.moved);
                const span = getNodeSpan(node);
                const invalidSides = getInvalidConnectionSides(node, nodeMap);

                if (!showNode) return null;

                return (
                  <div
                    className={`flow-page__node ${
                      selectedNodeIds.includes(node.id)
                        ? "flow-page__node--selected"
                        : ""
                    } ${activeNodeIds.has(node.id) ? "flow-page__node--active" : ""}`}
                    key={node.id}
                    style={{
                      left: `calc(50% + ${node.x * CELL_SIZE}px)`,
                      top: `calc(50% + ${node.y * CELL_SIZE}px)`,
                      width: `${CELL_SIZE * span.width}px`,
                      height: `${CELL_SIZE * span.height}px`,
                    }}
                    onPointerDown={(event) => handleNodePointerDown(event, node)}
                    onContextMenu={(event) => handleNodeContextMenu(event, node)}
                  >
                    <FlowNodeGraphic
                      node={node}
                      onRotate={() => handleRotateNode(node.id)}
                      rotateDisabled={!canRotateNode(node)}
                      active={activeNodeIds.has(node.id)}
                    />

                    {getAvailablePorts(node, nodeMap).map((side) => (
                      <button
                        key={`${node.id}-${side}`}
                        type="button"
                        className={`flow-page__port-hint flow-page__port-hint--${side}`}
                        onClick={(event) => handleOpenConnectionMenu(event, node.id, side)}
                      ></button>
                    ))}

                    {invalidSides.map((side) => (
                      <span
                        key={`${node.id}-invalid-${side}`}
                        className={`flow-page__invalid-port flow-page__invalid-port--${side}`}
                        title="Invalid connection"
                      >
                        <FaIcons.FaTimes />
                      </span>
                    ))}

                    {node.locked ? (
                      <span className="flow-page__lock-indicator" title="Locked">
                        <FaIcons.FaLock />
                      </span>
                    ) : null}

                    {node.startFlow ? (
                      <span className="flow-page__start-indicator" title="Start flow">
                        <FaIcons.FaPlay />
                      </span>
                    ) : null}

                    {node.label ? (
                      <span className="flow-page__node-label">{node.label}</span>
                    ) : null}
                  </div>
                );
              })}
            </div>

            {selectedNodeIds.length > 0 && !contextMenu && selectedNodeScreenPosition ? (
              <div
                className="flow-page__floating-panel flow-page__dialog"
                style={selectedNodeScreenPosition}
                ref={floatingPanelRef}
              >
                <button
                  type="button"
                  className="flow-page__floating-panel__close"
                  onClick={() => setSelectedNodeIds([])}
                >
                  <FaIcons.FaTimes />
                </button>
                <div className="flow-page__dialog-header">
                  <div>
                    <span className="flow-page__dialog-kicker">Component</span>
                    <h4>
                      {selectedNodeType
                        ? selectedNodeIds.length > 1
                          ? `${selectedNodeIds.length} ${selectedNodeType}`
                          : selectedNodeType
                        : `${selectedNodeIds.length} components`}
                    </h4>
                  </div>
                </div>
                <div className="flow-page__floating-panel__fields">
                  {panelFields.map((field) => (
                    <label
                      key={field.key}
                      className="flow-page__floating-panel__field"
                    >
                      <span>{field.label}</span>
                      {field.inputType === "select" ? (
                        <select
                          value={(() => {
                            if (selectedNodes.length === 0) return "";
                            const firstValue = selectedNodes[0].values[field.key] ?? "";
                            return selectedNodes.every(
                              (node) => (node.values[field.key] ?? "") === firstValue
                            )
                              ? firstValue
                              : firstValue;
                          })()}
                          onChange={(event) =>
                            handleNodeValueChange(field.key, event.target.value)
                          }
                        >
                          {(field.options ?? []).map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type={field.inputType === "color" ? "color" : "text"}
                          value={(() => {
                            if (selectedNodes.length === 0) return "";
                            if (field.key === "color") {
                              const firstValue = selectedNodes[0].color;
                              return selectedNodes.every((node) => node.color === firstValue)
                                ? firstValue
                                : "#9ea4ab";
                            }
                            if (field.key === "label") {
                              const firstValue = selectedNodes[0].label;
                              return selectedNodes.every((node) => node.label === firstValue)
                                ? firstValue
                                : "";
                            }

                            const firstValue = selectedNodes[0].values[field.key] ?? "";
                            return selectedNodes.every(
                              (node) => (node.values[field.key] ?? "") === firstValue
                            )
                              ? firstValue
                              : "";
                          })()}
                          onChange={(event) =>
                            handleNodeValueChange(field.key, event.target.value)
                          }
                        />
                      )}
                    </label>
                  ))}
                </div>
                {selectedNodeType === "ballValve" && selectedNode ? (
                  <div className="flow-page__floating-panel__actions">
                    <button
                      type="button"
                      className="rectangular-button"
                      onClick={() => handleToggleBallValve(selectedNode.id)}
                    >
                      {isBallValveClosed(selectedNode) ? "Open valve" : "Close valve"}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="flow-page__transport">
              <button type="button" onClick={() => setPlaybackIndex(0)}>
                <FaIcons.FaFastBackward />
              </button>
              <button
                type="button"
                onClick={() => setPlaybackIndex((current) => Math.max(current - 1, 0))}
              >
                <FaIcons.FaStepBackward />
              </button>
              <button
                type="button"
                className="flow-page__transport__play"
                onClick={() => setIsPlaying((current) => !current)}
              >
                {isPlaying ? <FaIcons.FaPause /> : <FaIcons.FaPlay />}
              </button>
              <button
                type="button"
                onClick={() =>
                  setPlaybackIndex((current) =>
                    Math.min(current + 1, Math.max(playbackSteps.length - 1, 0))
                  )
                }
              >
                <FaIcons.FaStepForward />
              </button>
              <button
                type="button"
                onClick={() => setPlaybackIndex(Math.max(playbackSteps.length - 1, 0))}
              >
                <FaIcons.FaFastForward />
              </button>
            </div>
          </div>

          <aside className="flow-page__sidebar">
            <h3>components</h3>
            <p>
              Drag parts into the boundary, move them, connect them from the green
              edge points, and your work stays in local storage.
            </p>

            <div className="flow-page__component-browser">
              <div className="flow-page__component-search">
                <input
                  type="text"
                  value={componentSearch}
                  onChange={(event) => setComponentSearch(event.target.value)}
                  placeholder="Search components"
                />
              </div>

              <div className="flow-page__component-list">
                {filteredSidebarSections.map((section) => (
                  <div key={section.key} className="flow-page__component-section">
                    <h4>{section.title}</h4>
                    <div className="flow-page__component-section__items">
                      {section.components.map((component) => (
                        <button
                          key={component.type}
                          type="button"
                          className="flow-page__component-card"
                          onPointerDown={(event) =>
                            handleStartPaletteDrag(event, component.type)
                          }
                        >
                          <span className="flow-page__component-card__preview">
                            <FlowNodeGraphic node={createPreviewNode(component.type)} showRotate={false} />
                          </span>
                          <span className="flow-page__component-card__label">
                            {component.title}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}

                {filteredSidebarSections.length === 0 ? (
                  <div className="flow-page__component-empty">No components found.</div>
                ) : null}
              </div>
            </div>
          </aside>
        </div>
      </section>

      {paletteDrag ? (
        <div
          className="flow-page__drag-ghost"
          style={{
            left: paletteDrag.clientX,
            top: paletteDrag.clientY,
            width: `${CELL_SIZE * zoom}px`,
            height: `${CELL_SIZE * zoom}px`,
          }}
        >
          <FlowNodeGraphic node={createPreviewNode(paletteDrag.type)} showRotate={false} />
        </div>
      ) : null}

      {moveDrag && moveDrag.moved ? (
        nodes
          .filter((node) => moveDrag.nodeIds.includes(node.id))
          .map((node) => {
            const anchorNode = nodes.find((candidate) => candidate.id === moveDrag.nodeId) ?? node;
            const span = getNodeSpan(node);

            return (
              <div
                key={`ghost-${node.id}`}
                className="flow-page__drag-ghost flow-page__drag-ghost--move"
                style={{
                  left:
                    moveDrag.clientX -
                    moveDrag.offsetX +
                    (node.x - anchorNode.x) * CELL_SIZE * zoom,
                  top:
                    moveDrag.clientY -
                    moveDrag.offsetY +
                    (node.y - anchorNode.y) * CELL_SIZE * zoom,
                  width: `${CELL_SIZE * span.width * zoom}px`,
                  height: `${CELL_SIZE * span.height * zoom}px`,
                }}
              >
                <FlowNodeGraphic node={node} showRotate={false} />
              </div>
            );
          })
      ) : null}

      {contextMenu ? (
        <div
          className="flow-page__context-menu flow-page__dialog"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          ref={contextMenuRef}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <div className="flow-page__dialog-header">
            <div>
              <span className="flow-page__dialog-kicker">Actions</span>
              <h5>Component menu</h5>
            </div>
          </div>
          <button
            type="button"
            className="rectangular-button"
            onClick={() => handleRemoveNode(contextMenu.nodeIds)}
          >
            <FaIcons.FaTrashAlt />
            Remove component
          </button>
          <button
            type="button"
            className="rectangular-button"
            onClick={() => handleToggleLockNode(contextMenu.nodeIds)}
          >
            {(contextMenu.nodeIds.every(
              (nodeId) => nodes.find((node) => node.id === nodeId)?.locked ?? false
            )) ? (
              <FaIcons.FaLockOpen />
            ) : (
              <FaIcons.FaLock />
            )}
            {(contextMenu.nodeIds.every(
              (nodeId) => nodes.find((node) => node.id === nodeId)?.locked ?? false
            ))
              ? "Unlock component"
              : "Lock component"}
          </button>
          <button
            type="button"
            className="rectangular-button"
            onClick={() => handleToggleStartFlow(contextMenu.nodeIds)}
          >
            <FaIcons.FaPlay />
            {(contextMenu.nodeIds.every(
              (nodeId) => nodes.find((node) => node.id === nodeId)?.startFlow ?? false
            ))
              ? "Remove start flow"
              : "Add start flow"}
          </button>
          <button
            type="button"
            className="rectangular-button"
            onClick={() => {
              removeAppDataFromLocalStorage(FLOW_WORKSPACE_ID);
              setNodes([]);
              setSelectedNodeIds([]);
              setContextMenu(null);
            }}
          >
            <FaIcons.FaTrash />
            Clear workspace
          </button>
        </div>
      ) : null}

      {connectionMenu ? (
        <div
          className="flow-page__connection-menu flow-page__dialog"
          style={{ left: connectionMenu.x, top: connectionMenu.y }}
          ref={connectionMenuRef}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <div className="flow-page__dialog-header">
            <div>
              <span className="flow-page__dialog-kicker">Connect</span>
              <h5>Add connection</h5>
            </div>
          </div>
          <div className="flow-page__connection-menu__list">
            {FLOW_COMPONENT_DEFINITIONS.map((component) => (
              <button
                key={`${connectionMenu.nodeId}-${connectionMenu.side}-${component.type}`}
                type="button"
                className="rectangular-button"
                onClick={() => handleAddConnectedNode(component.type)}
              >
                <span>{component.title}</span>
                <FlowNodeGraphic
                  node={{ ...createPreviewNode(component.type), id: `preview-${component.type}` }}
                  showRotate={false}
                />
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
